import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart3, Download, Calendar, TrendingUp, Users, Award, Activity, MapPin, FileSpreadsheet } from 'lucide-react';
import { Card, Button, Badge, LoadingSpinner } from '../../components/common';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { useAdminReports, useExportData, useExportAllReports, useExportUnifiedReport } from '../../hooks';
import toast from 'react-hot-toast';

import QuickActions from '@/components/admin/QuickActions';

import { exportChartAsImage } from '@/utils/exportChartAsImage';

import ResponsivePieLabel from '@/components/admin/ResponsivePieLabel';

const AdminReports: React.FC = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('excel'); // Selector de formato
  const { data: reportData, isLoading } = useAdminReports(dateRange);
  const exportDataMutation = useExportData();
  const exportAllMutation = useExportAllReports();
  const exportUnifiedMutation = useExportUnifiedReport();

  // Paleta Brinsa para gráficos
  const BRINSA = {
    azul: '#001689',
    celeste: '#18f1d8',
    verde: '#00b93d',
    naranja: '#ff7a00',
    grisGrid: 'rgba(0,22,137,0.08)',
    morado: '#B32BA6',
  };

  const PIE_COLORS = [BRINSA.azul, BRINSA.verde, BRINSA.celeste, BRINSA.morado, BRINSA.naranja];

  const handleExport = async (type: 'users' | 'activities' | 'certificates') => {
    try {
      await exportDataMutation.mutateAsync({ type, dateRange, format: exportFormat });
    } catch (error) {
      console.error('Error exportando:', error);
    }
  };

  const handleExportAll = async () => {
    try {
      await exportAllMutation.mutateAsync({ dateRange, format: exportFormat });
    } catch (error) {
      console.error('Error exportando todos los reportes:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const {
    kpis = {},
    userGrowthData = [],
    activityData = [],
    usersByZone = [],
    activitiesByCategory = [],
    certificationProgress = []
  } = reportData || {};

  // Función para obtener la etiqueta del rango de fechas
  const getDateRangeLabel = (range: string): string => {
    const labels: Record<string, string> = {
      '7d': 'Últimos 7 días',
      '30d': 'Últimos 30 días',
      '90d': 'Últimos 90 días',
      '1y': 'Último año'
    };
    return labels[range] || range;
  };

  const kpiCards = [
    {
      title: 'Usuarios Activos',
      value: kpis.activeUsers || 0,
      subtitle: getDateRangeLabel(dateRange),
      icon: Users,
      chipBg: 'bg-brinsa-celeste/30',
      chipText: 'text-brinsa-azul'
    },
    {
      title: 'Sesiones Totales',
      value: kpis.totalSessions || 0,
      subtitle: getDateRangeLabel(dateRange),
      icon: TrendingUp,
      chipBg: 'bg-brinsa-verde/20',
      chipText: 'text-brinsa-verde'
    },
    {
      title: 'Certificados',
      value: kpis.totalCertificates || 0,
      subtitle: getDateRangeLabel(dateRange),
      icon: Award,
      chipBg: 'bg-brinsa-naranja/20',
      chipText: 'text-brinsa-naranja'
    },
    {
      title: 'Tasa Completitud',
      value: `${kpis.completionRate || 0}%`,
      subtitle: `De ${kpis.totalUsers || 0} usuarios totales`,
      icon: BarChart3,
      chipBg: 'bg-brinsa-azul/10',
      chipText: 'text-brinsa-azul'
    }
  ];

  const exportReports = [
    {
      name: 'Reporte de Usuarios',
      description: 'Lista completa de usuarios con estadísticas',
      format: exportFormat.toUpperCase(),
      type: 'users' as const,
      icon: Users,
      size: `${Math.round((kpis.totalUsers || 0) * 0.1)}KB`
    },
    {
      name: 'Actividades por Categoría',
      description: 'Detalle de actividades completadas por categoría',
      format: exportFormat.toUpperCase(),
      type: 'activities' as const,
      icon: Activity,
      size: `${Math.round((kpis.totalSessions || 0) * 0.05)}KB`
    },
    {
      name: 'Certificados Emitidos',
      description: 'Lista de todos los certificados generados',
      format: exportFormat.toUpperCase(),
      type: 'certificates' as const,
      icon: Award,
      size: `${Math.round((kpis.totalCertificates || 0) * 0.2)}KB`
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-brinsa-azul mb-2">
              Reportes y Análisis
            </h1>
            <p className="text-lg text-brinsa-azulOscuro/80">
              Métricas detalladas del uso de la plataforma
            </p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Selector de formato CSV/Excel */}
            <div className="flex items-center space-x-2 border border-brinsa-celeste/40 rounded-lg px-3 py-2">
              <FileSpreadsheet className="w-4 h-4 text-brinsa-azul" />
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'excel')}
                className="text-sm bg-transparent border-none focus:ring-0 focus:outline-none text-brinsa-azul font-medium cursor-pointer"
              >
                <option value="excel">Excel (.xlsx)</option>
                <option value="csv">CSV (.csv)</option>
              </select>
            </div>

            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm border-brinsa-celeste/40 focus:ring-2 focus:ring-brinsa-azul focus:border-brinsa-azul"
            >
              <option value="7d">Últimos 7 días</option>
              <option value="30d">Últimos 30 días</option>
              <option value="90d">Últimos 90 días</option>
              <option value="1y">Último año</option>
            </select>

            <Button
              variant="outline"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleExportAll}
              disabled={exportAllMutation.isPending}
            >
              {exportAllMutation.isPending ? 'Exportando...' : 'Exportar Todos'}
            </Button>

            <Button
              variant="primary"
              size="sm"
              leftIcon={<FileSpreadsheet className="w-4 h-4" />}
              onClick={() => exportUnifiedMutation.mutateAsync({ dateRange })}
              disabled={exportUnifiedMutation.isPending}
              className="whitespace-nowrap"
            >
              {exportUnifiedMutation.isPending ? 'Generando...' : 'Reporte Unificado'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* KPIs principales - SIN porcentajes falsos */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        {kpiCards.map((kpi) => (
          <Card key={kpi.title} className="p-6 border border-brinsa-celeste/30 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-brinsa-azulOscuro/80 mb-1">{kpi.title}</p>
                <p className="text-3xl font-bold text-brinsa-azul mb-1">
                  {typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                </p>
                {/* Subtítulo informativo en lugar del porcentaje falso */}
                <p className="text-xs text-brinsa-azulOscuro/60">{kpi.subtitle}</p>
              </div>
              <div className={`w-12 h-12 ${kpi.chipBg} rounded-xl grid place-items-center flex-shrink-0`}>
                <kpi.icon className={`w-6 h-6 ${kpi.chipText}`} />
              </div>
            </div>
          </Card>
        ))}
      </motion.div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Crecimiento de usuarios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Card title="Ingresos a la Plataforma (últimos 7 días)" className="border border-brinsa-celeste/30">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-usuarios", "ingresos_plataforma.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div id="chart-usuarios" className="h-80 bg-white p-2 rounded-md">
              {userGrowthData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowthData}>
                    <CartesianGrid stroke={BRINSA.grisGrid} strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fill: '#516173' }} />
                    <YAxis tick={{ fill: '#516173' }} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="users"
                      stroke={BRINSA.azul}
                      strokeWidth={3}
                      dot={{ r: 4, stroke: BRINSA.celeste, strokeWidth: 2, fill: '#fff' }}
                      activeDot={{ r: 6, fill: BRINSA.celeste }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <TrendingUp className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay ingresos registrados en los últimos 7 días</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Actividad por tipo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Card title="Actividad por Tipo" className="border border-brinsa-celeste/30">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-actividad", "actividad_por_tipo.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div id="chart-actividad" className="h-80 bg-white p-2 rounded-md">
              {activityData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={activityData}>
                    <CartesianGrid stroke={BRINSA.grisGrid} strokeDasharray="3 3" />
                    <XAxis dataKey="day" tick={{ fill: '#516173' }} />
                    <YAxis tick={{ fill: '#516173' }} />
                    <Tooltip />
                    <Bar dataKey="videos" fill={BRINSA.naranja} name="Videos" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="ar" fill={BRINSA.celeste} name="AR" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="tests" fill={BRINSA.verde} name="Tests" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="portfolio" fill={BRINSA.azul} name="Portfolio" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay datos de actividad disponibles</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Gráficos secundarios */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usuarios por zona */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <Card title="Usuarios por Zona Geográfica" className="border border-brinsa-celeste/30">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-zonas", "usuarios_por_zona.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div
              id="chart-zonas"
              className="bg-white p-2 rounded-md"
              style={{ height: Math.max(320, usersByZone.length * 40 + 60) }}
            >
              {usersByZone.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={usersByZone
                      .map((item: any) => ({
                        name: item.zone || 'Sin especificar',
                        usuarios: parseInt(item.count),
                      }))
                      .sort((a: any, b: any) => b.usuarios - a.usuarios)}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid stroke={BRINSA.grisGrid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#516173', fontSize: 12 }} />
                    <YAxis
                      type="category"
                      dataKey="name"
                      tick={{ fill: '#516173', fontSize: 12 }}
                      width={120}
                    />
                    <Tooltip
                      formatter={(value: number) => [`${value} usuarios`, 'Cantidad']}
                    />
                    <Bar
                      dataKey="usuarios"
                      fill={BRINSA.azul}
                      radius={[0, 6, 6, 0]}
                      barSize={24}
                    >
                      {usersByZone.map((_entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay datos de zona geográfica</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>

        {/* Certificación por categoría */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Card title="Certificados por Categoría" className="border border-brinsa-celeste/30">
            <div className="flex justify-end mb-2">
              <button
                onClick={() => exportChartAsImage("chart-certificados", "certificados_por_categoria.png")}
                className="flex items-center gap-1 text-sm text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
              >
                <Download className="w-4 h-4" /> Exportar Imagen
              </button>
            </div>

            <div id="chart-certificados" className="h-80 bg-white p-2 rounded-md">
              {certificationProgress.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={certificationProgress.map((item: any) => ({
                      name: item.categoryName,
                      certificates: parseInt(item.certificates),
                    }))}
                  >
                    <CartesianGrid stroke={BRINSA.grisGrid} strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fill: '#516173' }} />
                    <YAxis tick={{ fill: '#516173' }} />
                    <Tooltip />
                    <Bar dataKey="certificates" fill={BRINSA.verde} radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-brinsa-azulOscuro/70">
                  <div className="text-center">
                    <Award className="w-12 h-12 mx-auto mb-4 text-brinsa-azul/40" />
                    <p>No hay datos de certificación disponibles</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Tabla de reportes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <Card title="Reportes Disponibles" className="border border-brinsa-celeste/30">
          <div className="space-y-4">
            {exportReports.map((report, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border border-brinsa-celeste/30 rounded-lg hover:bg-brinsa-celeste/5 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-brinsa-celeste/30 rounded-lg grid place-items-center">
                    <report.icon className="w-5 h-5 text-brinsa-azul" />
                  </div>
                  <div>
                    <h3 className="font-medium text-brinsa-azul">{report.name}</h3>
                    <p className="text-sm text-brinsa-azulOscuro/80">{report.description}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <Badge variant="default" size="sm">{report.format}</Badge>
                      <span className="text-xs text-brinsa-azulOscuro/60">~{report.size}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport(report.type)}
                  disabled={exportDataMutation.isPending}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportDataMutation.isPending ? 'Descargando...' : 'Descargar'}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>
      <QuickActions />
    </div>
  );
};

export default AdminReports;